import { type DirectoryWalkEntry } from "./recursiveDirectoryWalk";

/** Minimal folder shape from document search (IATA tree + files). */
export interface AppFolderNode {
  name: string;
  children: AppFolderNode[];
  documents: { fileName: string }[];
}

export interface CompareWithAppFoldersResult {
  missingInApp: DirectoryWalkEntry[];
  onlyInApp: DirectoryWalkEntry[];
  localFileCount: number;
  appFileCount: number;
  missingFileCount: number;
  onlyInAppFileCount: number;
}

/** Case-insensitive path key; ignores spaces so "A. Certificates" matches "A.Certificates". */
export function toComparablePathKey(relativePath: string): string {
  return relativePath
    .replace(/\\/g, "/")
    .split("/")
    .filter(Boolean)
    .map((segment) => segment.replace(/\s+/g, "").toLowerCase())
    .join("/");
}

/**
 * Strips the picked folder's own name from the start of a path (e.g. "components/A. Certs/x.pdf"
 * with root "components" -> "A. Certs/x.pdf"). Files directly under the picked folder
 * ("components/x.pdf" -> "x.pdf") are kept instead of being dropped.
 */
export function stripKnownRootPrefix(relativePath: string, pickerRootName?: string | null): string {
  const normalized = relativePath.replace(/\\/g, "/").replace(/^\/+|\/+$/g, "");
  const root = pickerRootName?.trim();
  if (!root) {
    return normalized;
  }

  if (normalized === root) {
    return "";
  }

  const prefix = `${root}/`;
  if (normalized.startsWith(prefix)) {
    return normalized.slice(prefix.length);
  }

  return normalized;
}

function collectAppFiles(
  folders: AppFolderNode[],
  parentPath = ""
): Map<string, DirectoryWalkEntry> {
  const files = new Map<string, DirectoryWalkEntry>();

  for (let folderIndex = 0; folderIndex < folders.length; folderIndex += 1) {
    const folder = folders[folderIndex];
    const folderPath = parentPath ? `${parentPath}/${folder.name}` : folder.name;

    for (let docIndex = 0; docIndex < folder.documents.length; docIndex += 1) {
      const doc = folder.documents[docIndex];
      const relativePath = `${folderPath}/${doc.fileName}`;
      const key = toComparablePathKey(relativePath);
      files.set(key, {
        kind: "file",
        name: doc.fileName,
        relativePath,
      });
    }

    const nested = collectAppFiles(folder.children, folderPath);
    nested.forEach((entry, key) => {
      files.set(key, entry);
    });
  }

  return files;
}

function collectLocalFiles(
  localEntries: DirectoryWalkEntry[],
  pickerRootName?: string | null
): Map<string, DirectoryWalkEntry> {
  const files = new Map<string, DirectoryWalkEntry>();

  for (let index = 0; index < localEntries.length; index += 1) {
    const entry = localEntries[index];
    if (entry.kind !== "file") {
      continue;
    }

    const comparablePath = stripKnownRootPrefix(entry.relativePath, pickerRootName);
    if (!comparablePath) {
      continue;
    }

    const key = toComparablePathKey(comparablePath);
    files.set(key, {
      kind: "file",
      name: entry.name,
      relativePath: comparablePath,
      sizeBytes: entry.sizeBytes,
    });
  }

  return files;
}

function addFolderAncestors(files: DirectoryWalkEntry[]): DirectoryWalkEntry[] {
  const result = [...files];
  const seen = new Set(result.map((entry) => entry.relativePath.toLowerCase()));

  for (let fileIndex = 0; fileIndex < files.length; fileIndex += 1) {
    const parts = files[fileIndex].relativePath.split("/").filter(Boolean);
    if (parts.length <= 1) {
      continue;
    }

    let accumulated = parts[0];
    addFolderEntry(result, seen, accumulated, parts[0]);

    for (let partIndex = 1; partIndex < parts.length - 1; partIndex += 1) {
      accumulated = `${accumulated}/${parts[partIndex]}`;
      addFolderEntry(result, seen, accumulated, parts[partIndex]);
    }
  }

  return result;
}

function addFolderEntry(
  result: DirectoryWalkEntry[],
  seen: Set<string>,
  relativePath: string,
  name: string
): void {
  const key = relativePath.toLowerCase();
  if (seen.has(key)) {
    return;
  }
  seen.add(key);
  result.push({ kind: "directory", name, relativePath });
}

export function compareWithAppFolders(
  localEntries: DirectoryWalkEntry[],
  appFolders: AppFolderNode[],
  pickerRootName?: string | null,
  rootDocuments?: { fileName: string }[]
): CompareWithAppFoldersResult {
  const localFiles = collectLocalFiles(localEntries, pickerRootName);
  const appFiles = collectAppFiles(appFolders);

  // Files that live directly in the scoped folder (no sub-folder prefix) so a
  // local folder picked while navigated inside that folder compares correctly.
  if (rootDocuments) {
    for (let docIndex = 0; docIndex < rootDocuments.length; docIndex += 1) {
      const doc = rootDocuments[docIndex];
      const key = toComparablePathKey(doc.fileName);
      appFiles.set(key, {
        kind: "file",
        name: doc.fileName,
        relativePath: doc.fileName,
      });
    }
  }

  const missingFiles: DirectoryWalkEntry[] = [];
  localFiles.forEach((entry, key) => {
    if (!appFiles.has(key)) {
      missingFiles.push(entry);
    }
  });

  const onlyInAppFiles: DirectoryWalkEntry[] = [];
  appFiles.forEach((entry, key) => {
    if (!localFiles.has(key)) {
      onlyInAppFiles.push(entry);
    }
  });

  return {
    missingInApp: addFolderAncestors(missingFiles),
    onlyInApp: addFolderAncestors(onlyInAppFiles),
    localFileCount: localFiles.size,
    appFileCount: appFiles.size,
    missingFileCount: missingFiles.length,
    onlyInAppFileCount: onlyInAppFiles.length,
  };
}
